import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Auth } from '@angular/fire/auth';
import {
  ConnectionStatus,
  SSEEventType,
  DeviceStatusEvent,
  AlertEvent,
  NotificationSeenEvent,
  DeviceLogEvent,
  ScreenshotPoolEvent
} from '../models/sse-events.model';

/**
 * SSE Service
 * 
 * Manages the connection to the backend SSE endpoint and provides
 * observables for different event types.
 * 
 * Features:
 * - Automatic reconnection on connection loss with exponential backoff
 * - Typed event streams for each event type
 * - Connection state tracking (connecting, connected, disconnected, error)
 * - Error handling and user feedback
 * - Authentication token injection
 */
@Injectable({
  providedIn: 'root'
})
export class SSEService implements OnDestroy {
  private auth = inject(Auth);
  
  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: any;

  // Event streams
  private statusUpdates$ = new Subject<DeviceStatusEvent>();
  private alerts$ = new Subject<AlertEvent>();
  private notificationsSeen$ = new Subject<NotificationSeenEvent>();
  private deviceLogs$ = new Subject<DeviceLogEvent>();
  private screenshotUpdates$ = new Subject<ScreenshotPoolEvent>();

  // Connection state (enum instead of boolean)
  private connectionStatus$ = new BehaviorSubject<ConnectionStatus>('disconnected');
  private connectionError$ = new Subject<Error>();

  constructor() {
    console.log('[SSEService] Service initialized');
  }

  /**
   * Connect to the SSE stream with auth token
   */
  async connect(): Promise<void> {
    if (this.eventSource) {
      console.log('[SSEService] Already connected');
      return;
    }

    // Set connecting state
    this.connectionStatus$.next('connecting');
    console.log('[SSEService] Connection state: connecting');

    try {
      // Get auth token
      const token = await this.auth.currentUser?.getIdToken();
      if (!token) {
        console.warn('[SSEService] No auth token available, connecting without auth');
      }

      // Build URL with token
      const baseUrl = `${environment.api.baseUrl}/api/events`;
      const url = token ? `${baseUrl}?token=${token}` : baseUrl;
      console.log('[SSEService] Connecting to:', baseUrl, token ? '(with auth)' : '(no auth)');

      this.eventSource = new EventSource(url);

      // Connection opened
      this.eventSource.onopen = () => {
        console.log('[SSEService] Connection established');
        this.connectionStatus$.next('connected');
        this.reconnectAttempts = 0;
      };

      // Generic error handler
      this.eventSource.onerror = (error) => {
        console.error('[SSEService] Connection error:', error);
        this.connectionStatus$.next('disconnected');
        this.handleConnectionError();
      };

      // Listen for specific event types
      this.setupEventListeners();

    } catch (error) {
      console.error('[SSEService] Failed to create EventSource:', error);
      this.connectionError$.next(error as Error);
    }
  }

  /**
   * Setup listeners for all SSE event types
   */
  private setupEventListeners(): void {
    if (!this.eventSource) return;

    // Status Update Events
    this.eventSource.addEventListener(SSEEventType.STATUS_UPDATE, (event: MessageEvent) => {
      try {
        const data: DeviceStatusEvent = JSON.parse(event.data);
        console.log('[SSEService] Status update:', data);
        this.statusUpdates$.next(data);
      } catch (error) {
        console.error('[SSEService] Failed to parse status update:', error);
      }
    });

    // Alert Events
    this.eventSource.addEventListener(SSEEventType.ALERT, (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        // Handle both formats: {alert: {...}} or direct object
        const alert: AlertEvent = data.alert || data;
        console.log('[SSEService] Alert:', alert);
        this.alerts$.next(alert);
      } catch (error) {
        console.error('[SSEService] Failed to parse alert:', error);
      }
    });

    // Notification Seen Events
    this.eventSource.addEventListener(SSEEventType.NOTIFICATION_SEEN, (event: MessageEvent) => {
      try {
        const data: NotificationSeenEvent = JSON.parse(event.data);
        console.log('[SSEService] Notification seen:', data);
        this.notificationsSeen$.next(data);
      } catch (error) {
        console.error('[SSEService] Failed to parse notification seen:', error);
      }
    });

    // Device Log Events
    this.eventSource.addEventListener(SSEEventType.DEVICE_LOG, (event: MessageEvent) => {
      try {
        const data: DeviceLogEvent = JSON.parse(event.data);
        console.log('[SSEService] Device log:', data);
        this.deviceLogs$.next(data);
      } catch (error) {
        console.error('[SSEService] Failed to parse device log:', error);
      }
    });

    // Screenshot Pool Updated Events
    this.eventSource.addEventListener(SSEEventType.SCREENSHOT_POOL_UPDATED, (event: MessageEvent) => {
      try {
        const data: ScreenshotPoolEvent = JSON.parse(event.data);
        console.log('[SSEService] Screenshot updated:', data);
        this.screenshotUpdates$.next(data);
      } catch (error) {
        console.error('[SSEService] Failed to parse screenshot update:', error);
      }
    });

    // Generic message handler (for debugging)
    this.eventSource.onmessage = (event: MessageEvent) => {
      console.log('[SSEService] Generic message:', event.data);
    };
  }

  /**
   * Handle connection errors and attempt reconnection with exponential backoff
   */
  private handleConnectionError(): void {
    this.disconnect();

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s (max 16s)
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 16000);
      this.reconnectAttempts++;
      
      console.log(`[SSEService] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

      this.reconnectTimeout = setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.error('[SSEService] Max reconnection attempts reached');
      this.connectionStatus$.next('error');
      const error = new Error('Connection lost. Unable to reconnect to real-time updates. Please refresh the page.');
      this.connectionError$.next(error);
      this.showConnectionLostMessage();
    }
  }

  /**
   * Show user-friendly message when connection is lost
   */
  private showConnectionLostMessage(): void {
    // This will be caught by components subscribing to connectionErrors$
    console.error(
      '%c⚠️ Connection Lost',
      'color: #ef4444; font-size: 14px; font-weight: bold;',
      '\n\nReal-time updates are unavailable.\nPlease refresh the page to reconnect.'
    );
  }

  /**
   * Disconnect from the SSE stream
   */
  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      console.log('[SSEService] Disconnecting');
      this.eventSource.close();
      this.eventSource = null;
      this.connectionStatus$.next('disconnected');
    }
  }

  /**
   * Get status update events stream
   */
  getStatusUpdates(): Observable<DeviceStatusEvent> {
    return this.statusUpdates$.asObservable();
  }

  /**
   * Get alert events stream
   */
  getAlerts(): Observable<AlertEvent> {
    return this.alerts$.asObservable();
  }

  /**
   * Get notification seen events stream
   */
  getNotificationsSeen(): Observable<NotificationSeenEvent> {
    return this.notificationsSeen$.asObservable();
  }

  /**
   * Get device log events stream
   */
  getDeviceLogs(): Observable<DeviceLogEvent> {
    return this.deviceLogs$.asObservable();
  }

  /**
   * Get screenshot update events stream
   */
  getScreenshotUpdates(): Observable<ScreenshotPoolEvent> {
    return this.screenshotUpdates$.asObservable();
  }

  /**
   * Get connection state stream
   * Returns: 'connected' | 'connecting' | 'disconnected' | 'error'
   */
  getConnectionState(): Observable<ConnectionStatus> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Get current connection status (sync)
   */
  getCurrentStatus(): ConnectionStatus {
    return this.connectionStatus$.value;
  }

  /**
   * Get connection errors stream
   */
  getConnectionErrors(): Observable<Error> {
    return this.connectionError$.asObservable();
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.connectionStatus$.value === 'connected';
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.disconnect();
    this.statusUpdates$.complete();
    this.alerts$.complete();
    this.notificationsSeen$.complete();
    this.deviceLogs$.complete();
    this.screenshotUpdates$.complete();
    this.connectionStatus$.complete();
    this.connectionError$.complete();
  }
}
